import { Navigate, Route, Routes } from 'react-router-dom';
import { CERTIFICATE_ROLES, CONTENT_ROLES, REQUEST_ROLES } from '@geoic/shared';
import { AppShell } from '@/components/layout/AppShell.js';
import { LoginPage } from '@/features/auth/LoginPage.js';
import { ProfilePage } from '@/features/auth/ProfilePage.js';
import { DashboardPage } from '@/features/dashboard/DashboardPage.js';
import { MediaPage } from '@/features/media/MediaPage.js';
import { SettingsPage } from '@/features/settings/SettingsPage.js';
import { UsersPage } from '@/features/users/UsersPage.js';
import { AuditPage } from '@/features/audit/AuditPage.js';
import { PagesListPage } from '@/features/content/pages/PagesListPage.js';
import { PageEditPage } from '@/features/content/pages/PageEditPage.js';
import {
  NewsCategoriesListPage,
  NewsCategoryEditPage,
  NewsEditPage,
  NewsListPage,
} from '@/features/content/news/NewsPages.js';
import { ServiceEditPage, ServicesListPage } from '@/features/content/services/ServicesPages.js';
import { CourseEditPage, CoursesListPage } from '@/features/content/courses/CoursesPages.js';
import { ProjectEditPage, ProjectsListPage } from '@/features/content/projects/ProjectsPages.js';
import {
  DroneShowPackageEditPage,
  DroneShowPackagesListPage,
  DroneShowProjectEditPage,
  DroneShowProjectsListPage,
} from '@/features/content/droneshow/DroneShowPages.js';
import { StaffEditPage, StaffListPage } from '@/features/content/staff/StaffPages.js';
import {
  DocumentCategoriesListPage,
  DocumentCategoryEditPage,
  DocumentEditPage,
  DocumentsListPage,
} from '@/features/content/documents/DocumentsPages.js';
import { MenusPage } from '@/features/content/menus/MenusPage.js';
import {
  BrandEditPage,
  BrandsListPage,
  CategoriesListPage,
  CategoryEditPage,
  ProductsListPage,
} from '@/features/shop/ShopListPages.js';
import { ProductEditPage } from '@/features/shop/ProductEditPage.js';
import { PricesPage } from '@/features/shop/PricesPage.js';
import { RequestsPage } from '@/features/requests/RequestsPage.js';
import { RequestDetailPage } from '@/features/requests/RequestDetailPage.js';
import { CertificatesPage } from '@/features/certificates/CertificatesPage.js';
import { CertificateDetailPage } from '@/features/certificates/CertificateDetailPage.js';
import { NAV_ITEMS } from './navigation.js';
import { RequireAuth, RequireRole } from './RequireAuth.js';
import { StagePlaceholder } from './StagePlaceholder.js';

const ADMIN_ROLES = ['superadmin', 'admin'] as const;

/**
 * Kontent bo'limlari (B7).
 *
 * Ro'yxat + tahrirlash juftligi 11 marta takrorlanadi. Ro'yxat qilib
 * berilsa, yangi bo'lim qo'shishda bittasi (odatda `:id`) unutilmaydi.
 */
const CONTENT_SECTIONS = [
  { path: 'pages', List: PagesListPage, Edit: PageEditPage },
  { path: 'news', List: NewsListPage, Edit: NewsEditPage },
  { path: 'news-categories', List: NewsCategoriesListPage, Edit: NewsCategoryEditPage },
  { path: 'services', List: ServicesListPage, Edit: ServiceEditPage },
  { path: 'courses', List: CoursesListPage, Edit: CourseEditPage },
  { path: 'projects', List: ProjectsListPage, Edit: ProjectEditPage },
  { path: 'droneshow/packages', List: DroneShowPackagesListPage, Edit: DroneShowPackageEditPage },
  { path: 'droneshow/projects', List: DroneShowProjectsListPage, Edit: DroneShowProjectEditPage },
  { path: 'staff', List: StaffListPage, Edit: StaffEditPage },
  { path: 'documents', List: DocumentsListPage, Edit: DocumentEditPage },
  { path: 'document-categories', List: DocumentCategoriesListPage, Edit: DocumentCategoryEditPage },
  // Do'kon (B8) — bir xil ro'yxat/tahrirlash juftligi
  { path: 'products', List: ProductsListPage, Edit: ProductEditPage },
  { path: 'categories', List: CategoriesListPage, Edit: CategoryEditPage },
  { path: 'brands', List: BrandsListPage, Edit: BrandEditPage },
] as const;

/**
 * Yo'llar.
 *
 * Hali qurilmagan bo'limlar (`stage` bilan belgilangan) `NAV_ITEMS` dan
 * avtomatik yasaladi — yangi bo'lim qo'shilganda yo'lni qo'lda yozish
 * unutilmaydi va menyu bosilganda 404 chiqmaydi.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="profile" element={<ProfilePage />} />

        {/*
          Maketda ariza kartochkasi ALOHIDA ekran («Arizalar ro'yxati»
          tugmasi bilan qaytiladi) — shuning uchun o'z manzili bor va
          uni xodim hamkasbiga havola qilib yubora oladi.
        */}
        <Route
          path="requests"
          element={
            <RequireRole roles={REQUEST_ROLES}>
              <RequestsPage />
            </RequireRole>
          }
        />
        <Route
          path="requests/:id"
          element={
            <RequireRole roles={REQUEST_ROLES}>
              <RequestDetailPage />
            </RequireRole>
          }
        />

        {/*
          Sertifikatlar (B17) — rasmiy hujjat, faqat `admin`/`superadmin`.
          Kartochka alohida ekran: bekor qilish va uzaytirish kabi amallar
          ro'yxatdan tasodifan bosilib ketmasin.
        */}
        <Route
          path="certificates"
          element={
            <RequireRole roles={CERTIFICATE_ROLES}>
              <CertificatesPage />
            </RequireRole>
          }
        />
        <Route
          path="certificates/:id"
          element={
            <RequireRole roles={CERTIFICATE_ROLES}>
              <CertificateDetailPage />
            </RequireRole>
          }
        />

        {/* Narxlar — `editor` ko'rmaydi (TZ 3.2) */}
        <Route
          path="prices"
          element={
            <RequireRole roles={ADMIN_ROLES}>
              <PricesPage />
            </RequireRole>
          }
        />

        <Route
          path="media"
          element={
            <RequireRole roles={CONTENT_ROLES}>
              <MediaPage />
            </RequireRole>
          }
        />

        <Route
          path="settings"
          element={
            <RequireRole roles={ADMIN_ROLES}>
              <SettingsPage />
            </RequireRole>
          }
        />

        <Route
          path="users"
          element={
            <RequireRole roles={['superadmin']}>
              <UsersPage />
            </RequireRole>
          }
        />

        <Route
          path="audit"
          element={
            <RequireRole roles={ADMIN_ROLES}>
              <AuditPage />
            </RequireRole>
          }
        />

        {/*
          Kontent bo'limlari (B7).

          Har biri ikkita yo'l: ro'yxat va tahrirlash. `new` — yangi yozuv;
          u alohida yo'l EMAS, chunki forma bir xil va faqat `id === 'new'`
          bilan farq qiladi.
        */}
        {CONTENT_SECTIONS.map(({ path, List, Edit }) => (
          <Route key={path} path={path} element={<RequireRole roles={CONTENT_ROLES} />}>
            <Route index element={<List />} />
            <Route path=":id" element={<Edit />} />
          </Route>
        ))}

        {/* Menyudagi «Dron-shou» bandi paketlarga olib boradi. */}
        <Route path="droneshow" element={<Navigate to="/droneshow/packages" replace />} />

        <Route
          path="menus"
          element={
            <RequireRole roles={CONTENT_ROLES}>
              <MenusPage />
            </RequireRole>
          }
        />

        {NAV_ITEMS.filter((item) => item.stage).map((item) => (
          <Route
            key={item.path}
            path={item.path.slice(1)}
            element={
              <RequireRole roles={item.roles}>
                <StagePlaceholder title={item.label} stage={item.stage as 'B7' | 'B8'} />
              </RequireRole>
            }
          />
        ))}
      </Route>

      {/* Noma'lum manzil — bosh sahifaga. Admin panelda 404 sahifasi ortiqcha. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
